/**
 * Kwacha AI — Frontend API Client
 * 
 * Typed fetch wrapper for all Kwacha AI (FastAPI backend) endpoints.
 * Set VITE_API_URL in your .env to point to the backend.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CategorySuggestion {
  category: string;
  confidence: number;
  category_id?: string | null;
}

export interface CategorizeResponse {
  suggestion: CategorySuggestion;
  alternatives: CategorySuggestion[];
  model_version: "seed" | "personal";
  powered_by: string;
}

export interface TrainResponse {
  status: "trained" | "insufficient_data" | "error";
  accuracy: number | null;
  samples_used: number | null;
  message: string;
  powered_by: string;
}

export interface OCRResponse {
  merchant: string | null;
  total: number | null;
  date: string | null;      // ISO 8601 or null
  currency: string;
  suggested_category: string | null;
  suggested_description: string | null;
  confidence: number;
  raw_text: string;
  powered_by: string;
}

export interface ModelStatus {
  user_id: string;
  has_personal_model: boolean;
  model_version: "seed" | "personal";
  powered_by: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail ?? "Kwacha AI request failed");
  }
  return res.json() as Promise<T>;
}

// ── Kwacha AI Client ──────────────────────────────────────────────────────────

export const kwachaAI = {
  /**
   * Predict the spending category for a transaction description.
   * Call this as the user types (debounce to ~400ms).
   */
  categorize: (description: string, userId: string): Promise<CategorizeResponse> =>
    apiFetch("/api/ml/categorize", {
      method: "POST",
      body: JSON.stringify({ description, user_id: userId }),
    }),

  /**
   * Trigger retraining of the user's personal model.
   * Call after the user has saved ≥ 20 transactions.
   */
  trainModel: (userId: string): Promise<TrainResponse> =>
    apiFetch("/api/ml/train", {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    }),

  /**
   * Check whether a personal model exists for this user.
   */
  modelStatus: (userId: string): Promise<ModelStatus> =>
    apiFetch(`/api/ml/status/${userId}`),

  /**
   * Extract merchant, amount, and date from a receipt image.
   * Pass the Supabase Storage public URL of the compressed image.
   */
  scanReceipt: (imageUrl: string, userId: string): Promise<OCRResponse> =>
    apiFetch("/api/ocr/receipt", {
      method: "POST",
      body: JSON.stringify({ image_url: imageUrl, user_id: userId }),
    }),

  /**
   * Download a PDF invoice. Returns a Blob for browser download.
   */
  downloadInvoicePDF: async (invoiceId: string, userId: string): Promise<Blob> => {
    const res = await fetch(`${API_BASE}/api/pdf/invoice`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ invoice_id: invoiceId, user_id: userId }),
    });
    if (!res.ok) throw new Error("Invoice PDF generation failed");
    return res.blob();
  },

  /**
   * Download a PDF quote. Returns a Blob for browser download.
   */
  downloadQuotePDF: async (quoteId: string, userId: string): Promise<Blob> => {
    const res = await fetch(`${API_BASE}/api/pdf/quote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quote_id: quoteId, user_id: userId }),
    });
    if (!res.ok) throw new Error("Quote PDF generation failed");
    return res.blob();
  },

  /**
   * Get financial forecast for the next X days.
   */
  forecastSpending: (userId: string, days = 30): Promise<any> =>
    apiFetch("/api/analytics/forecast", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, days_to_forecast: days }),
    }),

  /**
   * Detect unusual spending patterns in historical data.
   */
  detectAnomalies: (userId: string, lookbackDays = 90): Promise<any> =>
    apiFetch(`/api/analytics/anomalies?user_id=${userId}&lookback_days=${lookbackDays}`),

  /**
   * Health check — verify the backend is reachable.
   */
  ping: (): Promise<{ service: string; status: string }> =>
    apiFetch("/health"),
};

// ── Group Savings API ─────────────────────────────────────────────────────────

export const groupsApi = {
  createGroup: (data: {
    user_id: string; name: string; description?: string;
    target_amount: number; deadline?: string; is_public?: boolean;
    min_contribution?: number; contribution_frequency?: string;
  }) => apiFetch("/api/groups/", { method: "POST", body: JSON.stringify(data) }),

  listGroups: (userId: string) =>
    apiFetch(`/api/groups/?user_id=${userId}`),

  generateInvite: (data: {
    group_id: string; invited_by: string; invited_email: string; message?: string;
  }): Promise<{ token: string; invite_url: string; expires_at: string }> =>
    apiFetch("/api/groups/invite", { method: "POST", body: JSON.stringify(data) }),

  joinGroup: (token: string, userId: string, name: string, email: string) =>
    apiFetch(`/api/groups/join/${token}?user_id=${userId}&name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`, { method: "POST" }),

  logContribution: (data: {
    group_id: string; member_id: string; amount: number; description?: string; method?: string;
  }) => apiFetch("/api/groups/contribute", { method: "POST", body: JSON.stringify(data) }),

  approveContribution: (data: {
    contribution_id: string; confirmed_by: string;
  }) => apiFetch("/api/groups/contributions/approve", { method: "POST", body: JSON.stringify(data) }),

  getStats: (groupId: string, userId: string) =>
    apiFetch(`/api/groups/stats/${groupId}?user_id=${userId}`),
};

/**
 * Utility: trigger a browser file download from a Blob.
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
